import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { notify } from "@/components/common/Notification";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import type {
  PaymentStatusRecord,
  PaymentTrackingStatus,
  ReceivedPaymentItem,
} from "./types";
import PaymentStatusSelect from "./PaymentStatusSelect";
import {
  applyReceivedItemsToPaymentStatus,
  formatDisplayDate,
  getPaymentStatusReceivedItems,
  getTotalReceivedAmount,
} from "./paymentStatusUtils";
import { inr } from "./utils";

interface PaymentStatusEditModalProps {
  open: boolean;
  record: PaymentStatusRecord | null;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (record: PaymentStatusRecord) => void | Promise<void>;
}

const ReadOnlyField = ({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) => (
  <div className="space-y-1">
    <Label className="text-muted-foreground text-xs">{label}</Label>
    <p className={`text-sm font-medium text-[#111827] ${className ?? ""}`}>
      {value}
    </p>
  </div>
);

const PaymentStatusEditModal = ({
  open,
  record,
  isSubmitting = false,
  onOpenChange,
  onSave,
}: PaymentStatusEditModalProps) => {
  const [status, setStatus] = useState<PaymentTrackingStatus>("Pending");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  const [receivedItems, setReceivedItems] = useState<ReceivedPaymentItem[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const resetReceivedItemForm = () => {
    setReceivedAmount("");
    setReceivedDate("");
    setEditingItemId(null);
  };

  useEffect(() => {
    if (!open || !record) {
      return;
    }

    setStatus(record.status);
    resetReceivedItemForm();
    setReceivedItems(getPaymentStatusReceivedItems(record));
  }, [open, record]);

  const handleAddOrUpdateReceivedItem = () => {
    const amount = Number(receivedAmount);
    if (!amount || amount <= 0) {
      notify.error("Please enter a valid received amount");
      return;
    }

    if (!receivedDate) {
      notify.error("Please select a received date");
      return;
    }

    if (editingItemId) {
      setReceivedItems(prev =>
        prev.map(item =>
          item.id === editingItemId
            ? { ...item, amount, date: receivedDate }
            : item
        )
      );
      notify.success("Received item updated");
      resetReceivedItemForm();
      return;
    }

    setReceivedItems(prev => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length + 1}`,
        amount,
        date: receivedDate,
      },
    ]);
    resetReceivedItemForm();
  };

  const handleEditReceivedItem = (item: ReceivedPaymentItem) => {
    setEditingItemId(item.id);
    setReceivedAmount(String(item.amount));
    setReceivedDate(item.date);
  };

  const handleRemoveReceivedItem = (id: string) => {
    setReceivedItems(prev => prev.filter(item => item.id !== id));
    if (editingItemId === id) {
      resetReceivedItemForm();
    }
  };

  const handleSave = async () => {
    if (!record) {
      return;
    }

    const updatedRecord = applyReceivedItemsToPaymentStatus(
      record,
      receivedItems,
      status
    );

    await onSave(updatedRecord);
  };

  if (!record) {
    return null;
  }

  const totalReceived = getTotalReceivedAmount(receivedItems);
  const pendingAmount = Math.max(record.total - totalReceived, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Payment Status</DialogTitle>
        </DialogHeader>

        <div className="-mr-2 space-y-5 overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-[#f8f8fe] p-4 md:grid-cols-3">
            <ReadOnlyField label="Invoice #" value={record.id} />
            <ReadOnlyField label="Client" value={record.client} />
            <ReadOnlyField
              label="Billing Period"
              value={record.period}
              className="text-blue-600"
            />
            <ReadOnlyField label="Amount" value={inr(record.amount)} />
            <ReadOnlyField label="Tax" value={inr(record.gst)} />
            <ReadOnlyField label="Total" value={inr(record.total)} />
            <ReadOnlyField label="Date" value={record.invoiceDate ?? "—"} />
            <ReadOnlyField
              label="Received Date"
              value={record.receivedDate ?? "—"}
            />
            <ReadOnlyField
              label="Pending"
              value={pendingAmount > 0 ? inr(pendingAmount) : "—"}
              className={pendingAmount > 0 ? "text-red-600" : undefined}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <PaymentStatusSelect
              value={status}
              onValueChange={setStatus}
              disabled={isSubmitting}
              triggerClassName="h-9 w-full bg-white px-3 text-xs"
              contentClassName="z-[110] bg-white"
            />
          </div>

          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <p className="text-sm font-semibold text-[#111827]">
              {editingItemId ? "Edit Received Item" : "Add Received Item"}
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Received Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Enter amount"
                  value={receivedAmount}
                  onChange={event => setReceivedAmount(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Received Date</Label>
                <Input
                  type="date"
                  value={receivedDate}
                  onChange={event => setReceivedDate(event.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={handleAddOrUpdateReceivedItem}
              >
                {editingItemId ? (
                  <>
                    <Pencil className="h-4 w-4" />
                    Update Received Item
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add Received Item
                  </>
                )}
              </Button>
              {editingItemId ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-2"
                  onClick={resetReceivedItemForm}
                >
                  <X className="h-4 w-4" />
                  Cancel Edit
                </Button>
              ) : null}
            </div>

            <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-[#f4f4f5]">
                  <TableRow className="bg-muted/60 hover:bg-muted/60">
                    <TableHead className="text-primary">#</TableHead>
                    <TableHead className="text-primary text-center">
                      Received Amount
                    </TableHead>
                    <TableHead className="text-primary text-center">
                      Received Date
                    </TableHead>
                    <TableHead className="text-primary text-right">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receivedItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-muted-foreground py-6 text-center text-sm"
                      >
                        No received items added yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    receivedItems.map((item, index) => (
                      <TableRow
                        key={item.id}
                        className={
                          editingItemId === item.id
                            ? "bg-primary/5 hover:bg-primary/5"
                            : undefined
                        }
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="text-center text-green-600">
                          {inr(item.amount)}
                        </TableCell>
                        <TableCell className="text-center">
                          {formatDisplayDate(item.date)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-primary hover:text-primary h-8 w-8"
                              onClick={() => handleEditReceivedItem(item)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700"
                              onClick={() => handleRemoveReceivedItem(item.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between rounded-md bg-[#f8f8fe] px-3 py-2 text-sm">
              <span className="text-muted-foreground">Total Received</span>
              <span className="font-semibold text-green-600">
                {inr(totalReceived)}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" disabled={isSubmitting} onClick={handleSave}>
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentStatusEditModal;
